import React, { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import SEO from '../../components/SEO';
import PublicHeader from '../../components/PublicHeader';
import PublicFooter from '../../components/PublicFooter';
import './PublicPages.css';

export default function RestaurantRoulettePage() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="public-page">
      <SEO
        title="Restaurant Roulette Alternative — Smarter Than Random Spinning | Hangrily"
        description="Looking for restaurant roulette? Hangrily is the smarter alternative. Instead of random spinning, swipe through nearby restaurants filtered by your preferences. Works for couples & groups. Real photos, ratings & distances. Free to try."
        path="/restaurant-roulette"
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: 'Restaurant Roulette Alternative - Hangrily',
            description: 'A smarter alternative to restaurant roulette that uses preferences and collaborative swiping.',
            url: 'https://hangrily.app/restaurant-roulette'
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://hangrily.app' },
              { '@type': 'ListItem', position: 2, name: 'Restaurant Roulette', item: 'https://hangrily.app/restaurant-roulette' }
            ]
          },
          {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: [
              {
                '@type': 'Question',
                name: 'What is restaurant roulette?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Restaurant roulette is a concept where you spin a wheel or use randomization to pick a restaurant. While fun, it\'s purely random — you might get fast food when you wanted fine dining. Hangrily is a smarter approach: it filters by your preferences first, then lets you swipe through real options with photos and ratings.'
                }
              },
              {
                '@type': 'Question',
                name: 'Is there an app better than restaurant roulette?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Yes — Hangrily replaces random roulette with intelligent matching. Instead of spinning a wheel, you set preferences (cuisine, price, distance, mood) and swipe through curated restaurants. For couples and groups, everyone swipes and the app finds consensus. You get a restaurant everyone actually wants, not a random pick.'
                }
              },
              {
                '@type': 'Question',
                name: 'Can I use restaurant roulette with a group?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Traditional roulette wheels don\'t support groups. Hangrily does. Up to 10 people join via a code, everyone swipes through restaurants independently, and the app finds the one with the most agreement. It\'s democratic and takes about 3 minutes.'
                }
              },
              {
                '@type': 'Question',
                name: 'Is Hangrily free to use?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Yes! Try Hangrily free with no account needed. Guest users get 3 sessions per week. Premium is $4.99/month for unlimited sessions, analytics, favorites, and rewards.'
                }
              }
            ]
          }
        ]}
      />
      <PublicHeader />

      <main className="page-content">
        <section className="page-hero">
          <h1>Restaurant Roulette — But Smarter</h1>
          <p className="hero-subtitle">
            Why spin a random wheel when you can swipe through real restaurants you'll actually love?
          </p>
        </section>

        <section className="content-section">
          <div className="two-column-layout">
            <div className="column-text">
              <h2>The Problem with Restaurant Roulette</h2>
              <p>
                Restaurant roulette sounds fun in theory — spin a wheel and let fate decide dinner. But here's the 
                reality: you spin, it lands on "Taco Bell," and everyone groans. Or it picks a sushi place when 
                half your group doesn't eat raw fish. <strong>Random doesn't mean good.</strong>
              </p>
              <p>
                Hangrily takes the fun concept of roulette and makes it actually work. Instead of random spinning, 
                you swipe through <strong>real restaurants near you</strong> — filtered by your preferences, with 
                photos, ratings, and prices. If you're with a group, everyone swipes and the app finds the restaurant 
                with the most consensus.
              </p>
              <p>
                Same element of discovery. Same fun. But you end up somewhere everyone actually wants to eat.
              </p>
              <button className="btn btn-primary btn-lg" onClick={() => navigate('/try')}>
                Try Smart Roulette Free →
              </button>
            </div>
            <div className="column-visual">
              <div className="stat-highlight-grid">
                <div className="stat-highlight">
                  <span className="stat-number-lg">0%</span>
                  <span className="stat-label-sm">Chance of landing on fast food you hate</span>
                </div>
                <div className="stat-highlight">
                  <span className="stat-number-lg">94%</span>
                  <span className="stat-label-sm">Users happy with their match</span>
                </div>
                <div className="stat-highlight">
                  <span className="stat-number-lg">15+</span>
                  <span className="stat-label-sm">Real restaurants per session</span>
                </div>
                <div className="stat-highlight">
                  <span className="stat-number-lg">10</span>
                  <span className="stat-label-sm">Max group size supported</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="content-section alt-bg">
          <h2 className="section-title">Restaurant Roulette vs. Hangrily</h2>
          <div className="comparison-grid">
            <div className="comparison-item lose">
              <h3>❌ Random Roulette Wheel</h3>
              <p>Picks randomly from a manually entered list. No photos, no ratings, no distance info. Doesn't consider anyone's preferences. Pure chance.</p>
            </div>
            <div className="comparison-item lose">
              <h3>❌ "Spin the Wheel" Apps</h3>
              <p>Slightly better — some pull from Google. But still random, no couple/group matching, no preference filtering. One person's pizza is another's nightmare.</p>
            </div>
            <div className="comparison-item lose">
              <h3>❌ Food Roulette Websites</h3>
              <p>Web-based random pickers. Usually outdated restaurant data, no location awareness, no group support. Fun novelty, terrible for actual dinner plans.</p>
            </div>
            <div className="comparison-item win">
              <h3>✅ Hangrily Smart Roulette</h3>
              <p>Real restaurant data with photos & ratings. Filtered by your preferences. Couple & group matching. Location-aware. Decision in 2 minutes. The roulette that actually works.</p>
            </div>
          </div>
        </section>

        <section className="content-section">
          <h2 className="section-title">How Hangrily's Smart Roulette Works</h2>
          <div className="numbered-steps">
            <div className="numbered-step">
              <div className="step-num">1</div>
              <div className="step-content">
                <h3>Set Your Filters (or Don't)</h3>
                <p>Pick your cuisine preferences, price range, and distance. Or leave everything open for maximum variety — like a curated roulette that only includes places you'd actually enjoy.</p>
              </div>
            </div>
            <div className="numbered-step">
              <div className="step-num">2</div>
              <div className="step-content">
                <h3>Swipe Through Discoveries</h3>
                <p>Restaurant cards appear one at a time with real photos, ratings, and details. Swipe right to keep, left to eliminate, or up to love. Each round narrows the field.</p>
              </div>
            </div>
            <div className="numbered-step">
              <div className="step-num">3</div>
              <div className="step-content">
                <h3>Get Your Winner</h3>
                <p>After up to 3 rounds, the algorithm reveals your top match. It's the excitement of roulette with the intelligence of a food critic. Get directions and go.</p>
              </div>
            </div>
          </div>
        </section>

        <section className="content-section alt-bg">
          <h2 className="section-title">Perfect for Every Group</h2>
          <div className="use-case-grid">
            <div className="use-case-card">
              <span className="use-case-icon">🍽️</span>
              <h3>Solo Discovery</h3>
              <p>Feeling adventurous? Use Hangrily like a personal food roulette — discover new spots filtered by your taste.</p>
              <Link to="/where-should-i-eat" className="use-case-link">Solo Mode →</Link>
            </div>
            <div className="use-case-card">
              <span className="use-case-icon">💑</span>
              <h3>Couple Date</h3>
              <p>Both swipe and only match on places you both want. It's roulette where both players win.</p>
              <Link to="/couples-restaurant-picker" className="use-case-link">Couples Mode →</Link>
            </div>
            <div className="use-case-card">
              <span className="use-case-icon">👥</span>
              <h3>Friend Group</h3>
              <p>Up to 10 friends all join and swipe. Democratic voting finds the restaurant everyone agrees on.</p>
              <Link to="/group-restaurant-decision" className="use-case-link">Group Mode →</Link>
            </div>
            <div className="use-case-card">
              <span className="use-case-icon">🎲</span>
              <h3>Surprise Me Mode</h3>
              <p>Leave all filters open and let Hangrily surprise you with nearby gems. Same thrill as roulette, zero risk of bad picks.</p>
              <Link to="/try" className="use-case-link">Try It Free →</Link>
            </div>
          </div>
        </section>

        <section className="content-section">
          <h2 className="section-title">Frequently Asked Questions</h2>
          <div className="faq-grid">
            <div className="faq-item">
              <h3>What is restaurant roulette?</h3>
              <p>Restaurant roulette randomly picks a restaurant for you, usually by spinning a wheel. It's fun but purely random — you might get a place nobody wants. Hangrily is the smarter alternative.</p>
            </div>
            <div className="faq-item">
              <h3>Is there something better than restaurant roulette?</h3>
              <p>Yes! Hangrily uses your preferences, real restaurant data, and group voting instead of randomness. Same fun discovery, but you always end up somewhere good.</p>
            </div>
            <div className="faq-item">
              <h3>Can groups use restaurant roulette?</h3>
              <p>Traditional roulette doesn't support groups. Hangrily does — up to 10 people join, everyone swipes, and the app finds consensus.</p>
            </div>
            <div className="faq-item">
              <h3>Is Hangrily free?</h3>
              <p>Try free with no account needed. 3 sessions per week for guests. Premium is $9.99/month for unlimited access.</p>
            </div>
          </div>
        </section>

        <section className="cta-banner">
          <h2>Smarter Than Roulette. Faster Than Yelp.</h2>
          <p>Discover your perfect restaurant in 2 minutes. Try free — no account needed.</p>
          <button className="btn btn-primary btn-lg" onClick={() => navigate('/try')}>
            Spin Up a Session Free →
          </button>
        </section>
      </main>

      <PublicFooter />
    </div>
  );
}
